import React, { useState, useMemo, useEffect, useContext } from 'react';
import {
  PageContainer, SidebarContainer, SidebarHeading, SidebarLink, SearchInput,
  MainContent, HeaderBar, HeaderTitle, HeaderActions,
  NoResultsMessage, ObservacionesLink, LoadingMessage
} from './RegistroPAI.styles';
import VaccineCard from '../../components/PAI/VaccineCard';
import { getTiposIdentificacion } from '../../api/patientApi';
import { storeRegistro, getPacienteCompleto, updatePaiHistorial } from '../../api/pai/paiApi';
import { toast } from 'react-toastify';
import { BtnGeneral } from '../../styles/Buttons';
import { usePaiConfig } from '../../hooks/pai/usePaiConfig';
import { calculateAge } from '../../utils/ageCalculator';
import { calculateGestationData } from "../../utils/gestationCalculator";
import { AuthContext } from '../../context/AuthContext';
import {
  mapApiPatientToForm,
  mapHistoricalData,
  preparePaiPayload,
  getCondicionUsuariaBlockStatus,
  isCondicionUsuariaBlocked
} from './paiUtils';

const VACCINE_LOTS_STORAGE_KEY = 'pai_recent_vaccine_lots_v1';
const MAX_RECENT_LOTS_PER_VACCINE = 3;
const VACCINE_LOT_FIELDS = ['lote', 'lote_jeringa', 'lote_diluyente'];

function loadRecentVaccineLots() {
  if (typeof window === 'undefined') return {};

  try {
    const raw = window.localStorage.getItem(VACCINE_LOTS_STORAGE_KEY);
    if (!raw) return {};

    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch (error) {
    console.error('Error cargando lotes recientes PAI:', error);
    return {};
  }
}

function saveRecentVaccineLots(lotsMap) {
  if (typeof window === 'undefined') return;

  try {
    window.localStorage.setItem(VACCINE_LOTS_STORAGE_KEY, JSON.stringify(lotsMap || {}));
  } catch (error) {
    console.error('Error guardando lotes recientes PAI:', error);
  }
}

function getRecentLotsForField(lotsMap, vaccineId, fieldKey) {
  const vaccineLots = lotsMap?.[vaccineId];

  if (Array.isArray(vaccineLots)) {
    return fieldKey === 'lote' ? vaccineLots : [];
  }

  return Array.isArray(vaccineLots?.[fieldKey]) ? vaccineLots[fieldKey] : [];
}

function mergeRecentLots(previousLots, vaccineId, fieldKey, lotValue) {
  const normalizedLot = typeof lotValue === 'string' ? lotValue.trim() : '';
  if (!vaccineId || !fieldKey || !normalizedLot) return previousLots;

  const vaccineLots = previousLots[vaccineId];
  const normalizedVaccineLots = Array.isArray(vaccineLots) ? { lote: vaccineLots } : (vaccineLots || {});
  const existingLots = Array.isArray(normalizedVaccineLots[fieldKey]) ? normalizedVaccineLots[fieldKey] : [];
  const nextLots = [normalizedLot, ...existingLots.filter((lot) => lot !== normalizedLot)]
    .slice(0, MAX_RECENT_LOTS_PER_VACCINE);

  return {
    ...previousLots,
    [vaccineId]: nextLots
      ? {
          ...normalizedVaccineLots,
          [fieldKey]: nextLots
        }
      : normalizedVaccineLots
  };
}

// ─── Campos de catálogo que se bloquean si vienen precargados ──
const LOCKABLE_CATALOG_FIELDS = ['aseguradora', 'pais_nacimiento', 'pais_residencia', 'depto_residencia', 'municipio_residencia'];

function getLockedCatalogFields(paciente) {
  const locked = {};
  if (!paciente) return locked;
  LOCKABLE_CATALOG_FIELDS.forEach((key) => {
    const val = paciente[key];
    if (val !== null && val !== undefined && String(val).trim() !== '') {
      locked[key] = true;
    }
  });
  return locked;
}

// ─── IDs seguros para anclaje HTML ────────────────────────────
function safeId(prefix, index) {
  return `${prefix}-${index}`;
}

// ─── Componente principal ──────────────────────────────────────
const RegistroPAI = () => {
  const { user } = useContext(AuthContext);
  // ─── Catálogos dinámicos desde BD (encapsulados en el hook) ─
  const { paiData, patientData, vacunadores, catalogos, configLoading } = usePaiConfig();

  // Estado del formulario
  const [formData, setFormData] = useState({});
  const [patientHistories, setPatientHistories] = useState({}); // Nuevo estado para historiales
  const [loading, setLoading] = useState(false);
  const [currentPatientId, setCurrentPatientId] = useState(null);
  const [showErrors, setShowErrors] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});
  const [errorSectionCursor, setErrorSectionCursor] = useState(null);
  const [expandTargetId, setExpandTargetId] = useState(null);
  // Campos de catálogo precargados desde el registro del paciente (se bloquean)
  const [lockedFields, setLockedFields] = useState({});

  // Tipos de ID nativos de BD
  const [tiposIdentificacion, setTiposIdentificacion] = useState([]);

  // Interactividad: Buscador de vacunas y Vacunas Seleccionadas
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedVaccines, setSelectedVaccines] = useState(() => new Set());
  const [recentVaccineLots, setRecentVaccineLots] = useState(() => loadRecentVaccineLots());

  // Identificadores de secciones clave del paciente
  const basicosSectionId = useMemo(() => {
    const sec = patientData.find(s => s.name.toUpperCase().includes('BÁSICOS') || s.name.toUpperCase().includes('BASICOS'));
    return sec ? sec.id : null;
  }, [patientData]);

  const complementariosSectionId = useMemo(() => {
    const sec = patientData.find(s => s.name.toUpperCase().includes('COMPLEMENTARIOS'));
    return sec ? sec.id : null;
  }, [patientData]);

  const condicionSectionId = useMemo(() => {
    const sec = patientData.find(s => s.name.toUpperCase().includes('CONDICION') || s.name.toUpperCase().includes('CONDICIÓN'));
    return sec ? sec.id : null;
  }, [patientData]);

  const condicionBlockStatus = useMemo(() => {
    const basicosData = basicosSectionId ? (formData[basicosSectionId] || {}) : {};
    const compData = complementariosSectionId ? (formData[complementariosSectionId] || {}) : {};
    return getCondicionUsuariaBlockStatus({ ...basicosData, ...compData });
  }, [basicosSectionId, complementariosSectionId, formData]);

  // Memorización y optimización (Vercel Best Practices)
  // rerender-memo: useCallback para evitar re-crear handlers en cada render
  // rerender-functional-setstate: Uso de setState funcional
  const handleFieldChange = React.useCallback((sectionId, fieldKey, value) => {
    setFormData(prev => {
      const updatedSection = { ...(prev[sectionId] || {}), [fieldKey]: value };

      // Al cambiar el departamento se limpia el municipio (select dependiente)
      if (fieldKey === 'depto_residencia' && prev[sectionId]?.depto_residencia !== value) {
        updatedSection['municipio_residencia'] = '';
      }

      // Cálculo de edad automático
      if (fieldKey === 'fecha_nacimiento') {
        if (value) {
          const { years, months, days, totalMonths } = calculateAge(value);
          updatedSection['edad_anios'] = years;
          updatedSection['edad_meses'] = months;
          updatedSection['edad_dias'] = days;
          updatedSection['total_meses'] = totalMonths;
        } else {
          updatedSection['edad_anios'] = '';
          updatedSection['edad_meses'] = '';
          updatedSection['edad_dias'] = '';
          updatedSection['total_meses'] = '';
        }
      }

      // ─── Lógica de Gestación (Semanas y FPP) ───
      const isGestante = updatedSection['condicion_usuaria'] === 'GESTANTES';
      const fum = updatedSection['fecha_ultima_menstruacion'];

      if (isGestante && fum && (fieldKey === 'fecha_ultima_menstruacion' || fieldKey === 'condicion_usuaria')) {
        const gestData = calculateGestationData(fum);
        Object.assign(updatedSection, gestData);
      }

      const nextState = {
        ...prev,
        [sectionId]: updatedSection
      };

      // Limpiar Condición de la Usuaria si pasa a estar bloqueada por sexo o edad
      const isPatientCoreSection = (basicosSectionId && sectionId === basicosSectionId) || (complementariosSectionId && sectionId === complementariosSectionId);
      if (isPatientCoreSection && condicionSectionId) {
        const combinedPatient = {
          ...(basicosSectionId ? (nextState[basicosSectionId] || {}) : {}),
          ...(complementariosSectionId ? (nextState[complementariosSectionId] || {}) : {})
        };
        if (isCondicionUsuariaBlocked(combinedPatient)) {
          nextState[condicionSectionId] = {};
        }
      }

      return nextState;
    });

    const isPatientCoreSection = (basicosSectionId && sectionId === basicosSectionId) || (complementariosSectionId && sectionId === complementariosSectionId);
    if (isPatientCoreSection && condicionSectionId && ['sexo', 'fecha_nacimiento', 'edad_anios'].includes(fieldKey)) {
      setFieldErrors(prevErr => {
        if (!prevErr[condicionSectionId]) return prevErr;
        const nextErr = { ...prevErr };
        delete nextErr[condicionSectionId];
        return nextErr;
      });
    }
  }, [basicosSectionId, complementariosSectionId, condicionSectionId]);

  // Lógica atómica de clonado masivo (lápiz)
  const handleCloneRecord = React.useCallback((sectionId, recordData) => {
    if (condicionSectionId && sectionId === condicionSectionId && condicionBlockStatus.blocked) {
      return;
    }
    setFormData(prev => ({
      ...prev,
      [sectionId]: {
        ...(prev[sectionId] || {}),
        ...recordData
      }
    }));
  }, [condicionSectionId, condicionBlockStatus.blocked]);

  // Detectar Blur para autocompletado
  const handleFieldBlur = React.useCallback(async (sectionId, fieldKey, value) => {
    if (fieldKey === 'num_identificacion' && value && value.length >= 5) {
      try {
        const res = await getPacienteCompleto(value);
        if (res.success && res.data) {
          toast.success("Paciente encontrado, historial precargado.", { position: "top-right", autoClose: 3000 });

          setFormData(prev => {
            const mappedUpdates = mapApiPatientToForm(res.data, patientData, prev);
            const nextState = {
              ...prev,
              ...mappedUpdates
            };
            const combinedPatient = {
              ...(basicosSectionId ? (nextState[basicosSectionId] || {}) : {}),
              ...(complementariosSectionId ? (nextState[complementariosSectionId] || {}) : {})
            };
            if (condicionSectionId && isCondicionUsuariaBlocked(combinedPatient)) {
              nextState[condicionSectionId] = {};
            }
            return nextState;
          });

          setPatientHistories(mapHistoricalData(res.data));
          setCurrentPatientId(value);
          setLockedFields({});
        } else {
          toast.info("Paciente no encontrado en el historial PAI ni en la base general.", { position: "top-right", autoClose: 3000 });
          setCurrentPatientId(null);
          setLockedFields({});
        }
      } catch (e) {
        console.error("Error buscando paciente:", e);
        toast.error("Error al consultar el paciente. Verifique su conexión.", { position: "top-right", autoClose: 3000 });
      }
    }
  }, [patientData, basicosSectionId, complementariosSectionId, condicionSectionId]);

  const handleVaccineSelect = React.useCallback((vaccineId, e) => {
    e.preventDefault();

    // js-set-map-lookups: Use Set/Map for O(1) lookups instead of Array.includes
    setSelectedVaccines(prev => {
      if (!prev.has(vaccineId)) {
        const newSet = new Set(prev);
        newSet.add(vaccineId);
        return newSet;
      }
      return prev;
    });

    // Desplazarse suavemente a la tarjeta después de un breve delay para que renderice
    setTimeout(() => {
      const element = document.getElementById(vaccineId);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 100);
  }, []);

  const handleVaccineRemove = React.useCallback((vaccineId) => {
    setSelectedVaccines(prev => {
      if (!prev.has(vaccineId)) return prev;
      const next = new Set(prev);
      next.delete(vaccineId);
      return next;
    });

    setFormData(prev => {
      if (!prev[vaccineId]) return prev;
      const next = { ...prev };
      delete next[vaccineId];
      return next;
    });

    setFieldErrors(prev => {
      if (!prev[vaccineId]) return prev;
      const next = { ...prev };
      delete next[vaccineId];
      return next;
    });
  }, []);

  const handleClearAll = React.useCallback((toastMessage) => {
    setFormData({});
    setSelectedVaccines(new Set());
    setPatientHistories({});
    setCurrentPatientId(null);
    setSearchTerm('');
    setShowErrors(false);
    setFieldErrors({});
    setErrorSectionCursor(null);
    setExpandTargetId(null);
    setLockedFields({});
    toast.info(toastMessage || 'Formulario limpiado');
  }, []);

  const handleUpdateHistoricalVaccine = React.useCallback(async (payload) => {
    const res = await updatePaiHistorial(payload);
    if (!res?.success) {
      throw new Error(res?.message || 'Error actualizando la vacuna');
    }

    if (currentPatientId) {
      try {
        const refreshed = await getPacienteCompleto(currentPatientId);
        if (refreshed?.success && refreshed.data) {
          setPatientHistories(mapHistoricalData(refreshed.data));
        }
      } catch (e) {
        console.error('Error refrescando histórico PAI:', e);
      }
    }

    return res.data;
  }, [currentPatientId]);

  useEffect(() => {
    saveRecentVaccineLots(recentVaccineLots);
  }, [recentVaccineLots]);

  // ─── Carga de tipos de identificación ─────────────────────────
  useEffect(() => {
    getTiposIdentificacion()
      .then(rs => { if (rs?.data) setTiposIdentificacion(rs.data); })
      .catch(err => console.error("Error cargando Tipos de ID:", err));
  }, []);

  // Filtrar vacunas en el sidebar
  const filteredVaccines = useMemo(() => {
    if (!searchTerm.trim()) return paiData;
    const lowerSearch = searchTerm.toLowerCase();
    return paiData.filter(v => v.name.toLowerCase().includes(lowerSearch));
  }, [searchTerm, paiData]);

  const isRequiredField = React.useCallback((field) => {
    if (!field) return false;
    if (typeof field.label !== 'string') return false;
    return field.label.includes('*');
  }, []);

  const isEmptyValue = React.useCallback((value) => {
    if (value === null || value === undefined) return true;
    if (typeof value === 'string') return value.trim() === '';
    return value === '';
  }, []);

  const validateForm = React.useCallback(() => {
    const isSectionActive = (item, sectionData) => {
      return (item.fields || []).some((field) => !isEmptyValue(sectionData[field.key]));
    };

    const shouldValidateSection = (item, sectionData) => {
      if (condicionSectionId && item?.id === condicionSectionId && condicionBlockStatus.blocked) {
        return false;
      }

      const tipo = Number(item?.idTipoObligatoriedad);

      if (Number.isNaN(tipo)) return true;
      if (tipo === 0) return false;
      if (tipo === 1) return true;
      if (tipo === 2) return isSectionActive(item, sectionData);

      return true;
    };

    const sectionsToValidate = [];

    patientData.forEach((section, idx) => {
      sectionsToValidate.push({
        item: section,
        dataSectionKey: section.id,
        domSectionId: safeId('patient', idx)
      });
    });

    paiData.forEach((vaccine) => {
      if (vaccine.grupo === 'group-3') return;
      if (!selectedVaccines.has(vaccine.id)) return;
      sectionsToValidate.push({
        item: vaccine,
        dataSectionKey: vaccine.id,
        domSectionId: vaccine.id
      });
    });

    paiData.filter(v => v.grupo === 'group-3').forEach((vaccine) => {
      sectionsToValidate.push({
        item: vaccine,
        dataSectionKey: vaccine.id,
        domSectionId: vaccine.id
      });
    });

    const nextErrors = {};
    let firstInvalidId = null;
    const missingBySectionKey = {};

    sectionsToValidate.forEach(({ item, dataSectionKey, domSectionId }) => {
      const sectionData = formData[dataSectionKey] || {};
      if (!shouldValidateSection(item, sectionData)) return;

      (item.fields || []).forEach((field, fieldIdx) => {
        if (!isRequiredField(field)) return;
        const value = sectionData[field.key];
        if (!isEmptyValue(value)) return;

        if (!nextErrors[dataSectionKey]) nextErrors[dataSectionKey] = {};
        nextErrors[dataSectionKey][field.key] = true;

        if (!missingBySectionKey[dataSectionKey]) {
          missingBySectionKey[dataSectionKey] = {
            sectionKey: dataSectionKey,
            sectionName: item.name,
          domSectionId,
            firstInvalidId: null,
            fields: []
          };
        }

        missingBySectionKey[dataSectionKey].fields.push(String(field.label || '').replace('*', '').trim());

        if (!missingBySectionKey[dataSectionKey].firstInvalidId) {
          missingBySectionKey[dataSectionKey].firstInvalidId = `${domSectionId}_${field.key}_${fieldIdx}`;
        }

        if (!firstInvalidId) {
          firstInvalidId = `${domSectionId}_${field.key}_${fieldIdx}`;
        }
      });
    });

    const missingSections = sectionsToValidate
      .filter(s => Boolean(missingBySectionKey[s.dataSectionKey]))
      .map(s => missingBySectionKey[s.dataSectionKey]);

    return { ok: Object.keys(nextErrors).length === 0, errors: nextErrors, firstInvalidId, missingSections };
  }, [condicionBlockStatus.blocked, condicionSectionId, formData, isEmptyValue, isRequiredField, paiData, patientData, selectedVaccines]);

  const getPatientIdentInfo = React.useCallback(() => {
    for (let i = 0; i < patientData.length; i += 1) {
      const section = patientData[i];
      const sectionData = formData[section.id] || {};
      const ident = sectionData.num_identificacion;
      if (!isEmptyValue(ident)) {
        return { value: ident, sectionIndex: i, sectionId: section.id };
      }
    }

    for (let i = 0; i < patientData.length; i += 1) {
      const section = patientData[i];
      const idx = (section.fields || []).findIndex(f => f.key === 'num_identificacion');
      if (idx >= 0) {
        return { value: null, sectionIndex: i, sectionId: section.id, fieldIndex: idx };
      }
    }

    return { value: null };
  }, [formData, isEmptyValue, patientData]);

  const handleSubmit = async () => {
    setLoading(true);
    try {
      setShowErrors(true);

      const patientIdentInfo = getPatientIdentInfo();
      if (!patientIdentInfo.value) {
        toast.error('Debe diligenciar la identificación del paciente para poder guardar el registro.');
        if (patientIdentInfo.sectionIndex !== undefined && patientIdentInfo.sectionIndex !== null) {
          setExpandTargetId(safeId('patient', patientIdentInfo.sectionIndex));
        }
        const targetId = (patientIdentInfo.sectionId && typeof patientIdentInfo.fieldIndex === 'number')
          ? `${safeId('patient', patientIdentInfo.sectionIndex)}_num_identificacion_${patientIdentInfo.fieldIndex}`
          : null;

        if (targetId) {
          setTimeout(() => {
            const el = document.getElementById(targetId);
            if (el) {
              el.scrollIntoView({ behavior: 'smooth', block: 'center' });
              el.focus?.();
            }
          }, 0);
        }

        setLoading(false);
        return;
      }

      if (selectedVaccines.size === 0) {
        toast.error('Debe seleccionar al menos una vacuna para poder guardar el registro.');
        setLoading(false);
        return;
      }

      const validation = validateForm();
      setFieldErrors(validation.errors);
      if (!validation.ok) {
        const missingSections = validation.missingSections || [];
        const cursorStillInvalid = errorSectionCursor && missingSections.some(s => s.sectionKey === errorSectionCursor);
        const nextCursor = cursorStillInvalid ? errorSectionCursor : (missingSections[0]?.sectionKey || null);
        setErrorSectionCursor(nextCursor);

        const currentSection = missingSections.find(s => s.sectionKey === nextCursor) || missingSections[0] || null;
        setExpandTargetId(currentSection?.domSectionId || null);

        if (currentSection) {
          const sectionMissingText = (currentSection.fields || []).map(f => `- ${f}`).join('\n');
          toast.error(
            <div style={{ whiteSpace: 'pre-line' }}>
              {`${currentSection.sectionName} - faltan campos obligatorios:\n${sectionMissingText}`}
            </div>
          );
        } else {
          toast.error('Faltan campos obligatorios por diligenciar.');
        }

        const targetId = currentSection?.firstInvalidId || validation.firstInvalidId;
        if (targetId) {
          setTimeout(() => {
            const el = document.getElementById(targetId);
            if (el) {
              el.scrollIntoView({ behavior: 'smooth', block: 'center' });
              el.focus?.();
            }
          }, 0);
        }
        setLoading(false);
        return;
      }

      setErrorSectionCursor(null);
      setExpandTargetId(null);

      const regObsConfig = paiData.find(v => v.name.toUpperCase().includes('REGISTRO Y OBS'));
      const registro_observacion = regObsConfig ? (formData[regObsConfig.id] || {}) : {};

      // Validación mandatoria decidida por negocio: Responsable (Vacunador)
      if (!registro_observacion.responsable) {
        toast.error("El responsable (vacunador) es obligatorio para poder guardar el registro.");
        setLoading(false);
        return;
      }

      const payload = preparePaiPayload(formData, selectedVaccines, paiData, patientData, registro_observacion);
      if (!Array.isArray(payload.esquema_vacunacion) || payload.esquema_vacunacion.length === 0) {
        toast.error('Debe registrar al menos una vacuna para poder guardar el registro.');
        setLoading(false);
        return;
      }

      // 4. Disparar API
      const res = await storeRegistro(payload);
      if (!res.success) throw new Error(res.message || 'Error al guardar el registro');

      toast.success(`Registrado exitosamente bajo ID Visita: ${res.data?.id_visita || res.data?.registro_id}`);

      const vaccineIdsToClear = Array.from(selectedVaccines);
      setRecentVaccineLots(prev => {
        let next = prev;

        vaccineIdsToClear.forEach((vaccineId) => {
          VACCINE_LOT_FIELDS.forEach((fieldKey) => {
            next = mergeRecentLots(next, vaccineId, fieldKey, formData[vaccineId]?.[fieldKey]);
          });
        });

        return next;
      });

      setFormData(prev => {
        if (vaccineIdsToClear.length === 0) return prev;
        const next = { ...prev };
        vaccineIdsToClear.forEach(id => {
          delete next[id];
        });
        return next;
      });
      setSelectedVaccines(new Set());
      setFieldErrors(prev => {
        if (vaccineIdsToClear.length === 0) return prev;
        const next = { ...prev };
        vaccineIdsToClear.forEach(id => {
          delete next[id];
        });
        return next;
      });

      if (currentPatientId) {
        try {
          const refreshed = await getPacienteCompleto(currentPatientId);
          if (refreshed?.success && refreshed.data) {
            setPatientHistories(mapHistoricalData(refreshed.data));
          }
        } catch (e) {
          console.error('Error refrescando histórico PAI:', e);
        }
      }

    } catch (err) {
      toast.error(err.message || 'Error al guardar el registro');
    } finally {
      setLoading(false);
    }
  };

  if (configLoading) {
    return (
      <PageContainer>
        <LoadingMessage>Cargando formulario PAI...</LoadingMessage>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      {/* ─── SIDEBAR ─────────────────────────────────────── */}
      <SidebarContainer>
        <SidebarHeading>Datos del Paciente</SidebarHeading>
        {patientData.map((section, idx) => (
          <SidebarLink
            key={section.id}
            href={`#${safeId('patient', idx)}`}
            $borderColor={section.color || '#e9ecef'}
            title={section.name}
          >
            {section.name}
          </SidebarLink>
        ))}

        <SidebarHeading $mt>Esquema de Vacunación</SidebarHeading>

        {/* Buscador de vacunas */}
        <SearchInput
          type="text"
          placeholder="Buscar vacuna..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />

        {filteredVaccines.map((vaccine) => {
          // Excluir grupo 3 (Registro y observaciones) de la lista buscable normal, ya que debe estar siempre al final
          if (vaccine.grupo === "group-3") return null;

          const hasHistory = (patientHistories.VACUNAS || []).some(v =>
            v.vacuna_nombre && v.vacuna_nombre.split(',').map(n => n.trim()).includes(vaccine.name)
          );

          return (
            <SidebarLink
              key={vaccine.id}
              href={`#${vaccine.id}`}
              onClick={(e) => handleVaccineSelect(vaccine.id, e)}
              $borderColor={vaccine.bg_color || '#e9ecef'}
              title={vaccine.name}
              style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '4px' }}
            >
              <span style={{ flex: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {vaccine.name}
              </span>
              {hasHistory && (
                <span
                  title="Tiene historial de vacunación previo"
                  style={{
                    fontSize: '10px',
                    backgroundColor: '#e3f2fd',
                    color: '#0d6efd',
                    padding: '2px 6px',
                    borderRadius: '10px',
                    fontWeight: 'bold',
                    whiteSpace: 'nowrap',
                    flexShrink: 0
                  }}
                >
                  Historial
                </span>
              )}
            </SidebarLink>
          );
        })}
        {filteredVaccines.length === 0 && (
          <NoResultsMessage>
            No se encontraron resultados
          </NoResultsMessage>
        )}

        {/* Mapear dinámicamente el grupo 3 para fijarlo al final del sidebar */}
        {paiData.filter(v => v.grupo === 'group-3').map(vaccine => (
          <ObservacionesLink
            key={vaccine.id}
            href={`#${vaccine.id}`}
            $borderColor={vaccine.bg_color || "#F0A7A7"}
            title={vaccine.name}
          >
            {vaccine.name}
          </ObservacionesLink>
        ))}
      </SidebarContainer>

      {/* ─── CONTENIDO PRINCIPAL ──────────────────────────── */}
      <MainContent>
        {/* Header sticky tipo navbar */}
        <HeaderBar>
          <HeaderTitle>Formulario Unificado PAI</HeaderTitle>
          <HeaderActions>
            <BtnGeneral
              as="button"
              type="button"
              onClick={() => handleClearAll('Nuevo registro')}
              disabled={loading}
              bg="gray"
              padding="4px 14px"
              fw="700"
            >
              Nuevo
            </BtnGeneral>
          </HeaderActions>
        </HeaderBar>

        {/* ── SECCIÓN: DATOS DEL PACIENTE (Acordeones permanentes) ── */}
        {patientData.map((section, idx) => {
          let sectionHistory = [];
          const uName = section.name.toUpperCase();
          const isCondicionSection = uName.includes('CONDICION') || uName.includes('CONDICIÓN');
          if (uName.includes('ANTECEDENTES MÉDICOS') || uName.includes('ANTECEDENTES M')) sectionHistory = patientHistories.ANTECEDENTES;
          else if (isCondicionSection) sectionHistory = patientHistories.CONDICION;
          else if (uName.includes('HISTORICO DE ANTECEDENTES') || uName.includes('HISTÓRICO DE ANTECEDENTES')) sectionHistory = patientHistories.HISTORICO;
          else if (uName.includes('CUIDADOR')) sectionHistory = patientHistories.CUIDADOR;
          else if (uName.includes('ESQUEMA')) sectionHistory = patientHistories.VACUNAS;

          return (
            <VaccineCard
              key={section.id}
              item={section}
              sectionId={safeId('patient', idx)}
              isPatient={true}
              defaultExpanded={section.name === "DATOS BÁSICOS"}
              forceExpand={expandTargetId === safeId('patient', idx)}
              currentUserId={user?.id}
              entryData={formData[section.id] || {}}
              onChange={(fieldKey, value) => handleFieldChange(section.id, fieldKey, value)}
              onClone={(recordData) => handleCloneRecord(section.id, recordData)}
              onBlur={(fieldKey, value) => handleFieldBlur(section.id, fieldKey, value)}
              tiposIdentificacion={tiposIdentificacion}
              vacunadores={vacunadores}
              historicalData={sectionHistory}
              showErrors={showErrors}
              fieldErrors={fieldErrors[section.id] || {}}
              onUpdateHistorical={handleUpdateHistoricalVaccine}
              disabled={isCondicionSection && condicionBlockStatus.blocked}
              disabledReason={isCondicionSection ? condicionBlockStatus.reason : ''}
              catalogos={catalogos}
              lockedFields={section.id === complementariosSectionId ? lockedFields : {}}
            />
          );
        })}

        {/* ── SECCIÓN: ESQUEMA DE VACUNACIÓN (Se añaden al dar clic) ── */}
        {paiData.map((vaccine) => {
          if (vaccine.grupo === 'group-3') return null; // Se renderiza aparte al final
          const isSelected = selectedVaccines.has(vaccine.id);
          if (!isSelected) return null;

          const sectionHistory = (patientHistories.VACUNAS || []).filter(v =>
            v.vacuna_nombre && v.vacuna_nombre.split(',').map(n => n.trim()).includes(vaccine.name)
          );
          return (
            <VaccineCard
              key={vaccine.id}
              item={vaccine}
              sectionId={vaccine.id}
              isPatient={false}
              defaultExpanded={true}
              forceExpand={expandTargetId === vaccine.id}
              currentUserId={user?.id}
              entryData={formData[vaccine.id] || {}}
              onChange={(fieldKey, value) => handleFieldChange(vaccine.id, fieldKey, value)}
              onClone={(recordData) => handleCloneRecord(vaccine.id, recordData)}
              tiposIdentificacion={tiposIdentificacion}
              vacunadores={vacunadores}
              catalogos={catalogos}
              fieldSuggestions={{
                lote: getRecentLotsForField(recentVaccineLots, vaccine.id, 'lote'),
                lote_jeringa: getRecentLotsForField(recentVaccineLots, vaccine.id, 'lote_jeringa'),
                lote_diluyente: getRecentLotsForField(recentVaccineLots, vaccine.id, 'lote_diluyente')
              }}
              historicalData={sectionHistory}
              removable={true}
              onRemove={() => handleVaccineRemove(vaccine.id)}
              showErrors={showErrors}
              fieldErrors={fieldErrors[vaccine.id] || {}}
              onUpdateHistorical={handleUpdateHistoricalVaccine}
            />
          );
        })}

        {/* ── SECCIÓN: REGISTRO Y OBSERVACIONES (Siempre visible al final) ── */}
        {paiData.filter(v => v.grupo === 'group-3').map((vaccine) => (
          <VaccineCard
            key={vaccine.id}
            item={vaccine}
            sectionId={vaccine.id}
            isPatient={false}
            defaultExpanded={true}
            forceExpand={expandTargetId === vaccine.id}
            currentUserId={user?.id}
            entryData={formData[vaccine.id] || {}}
            onChange={(fieldKey, value) => handleFieldChange(vaccine.id, fieldKey, value)}
            onClone={(recordData) => handleCloneRecord(vaccine.id, recordData)}
            tiposIdentificacion={tiposIdentificacion}
            vacunadores={vacunadores}
            catalogos={catalogos}
            historicalData={patientHistories.OBSERVACIONES || []}
            showErrors={showErrors}
            fieldErrors={fieldErrors[vaccine.id] || {}}
            onUpdateHistorical={handleUpdateHistoricalVaccine}
          />
        ))}

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '14px' }}>
          <BtnGeneral
            as="button"
            type="button"
            onClick={() => handleClearAll('Nuevo registro')}
            disabled={loading}
            bg="gray"
            padding="4px 14px"
            fw="700"
          >
            Nuevo
          </BtnGeneral>
          <BtnGeneral
            as="button"
            type="button"
            onClick={handleClearAll}
            disabled={loading}
            bg="gray"
            padding="4px 14px"
            fw="700"
          >
            Limpiar todo
          </BtnGeneral>
          <BtnGeneral
            as="button"
            type="button"
            onClick={handleSubmit}
            disabled={loading}
            bg="blue"
            padding="4px 14px"
            fw="700"
          >
            {loading ? 'Guardando...' : 'Guardar'}
          </BtnGeneral>
        </div>
      </MainContent>
    </PageContainer>
  );
};

export default RegistroPAI;
